import { Injectable } from '@angular/core';
import { FinancialCompany } from '@gipi-financial/company/models/company.model';
import { FinancialCompanyService } from '@gipi-financial/company/services/company.service';
import { FinancialProvider } from '@gipi-financial/provider/models/provider.model';
import { CustomAuthenticationService } from '@gipi-shared/services/custom-authentication.service';
import { ArrayUtil, CurrencyUtil, GIPIUuid, ObjectUtil, StringUtil } from '@gipisistemas/ng-core';

export interface PaymentVoucherData {
    provider: FinancialProvider;
    description: string;
    comments: string;
    amountPaid: number;
    paymentDate: Date;
    documentNumber: string;
    isTwoWays: boolean;
}

@Injectable({ providedIn: 'root' })
export class PaymentVoucherService {

    private _company: FinancialCompany = null;

    constructor(
        private _customAuthenticationService: CustomAuthenticationService,
        private _companyService: FinancialCompanyService,
    ) {
        this._findCompanyData();
    }

    private _findCompanyData(): void {
        try {
            const companyId: GIPIUuid = this._customAuthenticationService.token.user.currentCompany;
            this._companyService.findByIdIn([Number(companyId)]).toPromise().then(companyList => {
                if (!ArrayUtil.isEmpty(companyList)) {
                    this._company = companyList[0];
                }
            }, error => {
                throw new Error(error);
            });
        } catch (e) {
            throw new Error(e);
        }
    }

    public print(data: PaymentVoucherData): void {
        try {
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            if (!iframeDoc) throw new Error('Não foi possível acessar o iframe');

            const outerHtmlPaymentVoucher = this._outerHtmlPaymentVoucher(data);

            const style: string = `
                @import url('https://fonts.googleapis.com/css2?family=Roboto&display=swap');

                * {
                    font-family: "Roboto", sans-serif;
                }

                @media print {
                    .noPrint {
                        display: none;
                    }

                    .header,
                    .bprint,
                    .footer {
                        display: none !important;
                    }

                    .table-div {
                        font-size: 0.8em;
                    }
                }

                @page :footer {
                    display: none;
                }

                @page :header {
                    display: none;
                }

                :host {
                    margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                fieldset {
                    width: 750px;
                    margin: 10px auto;
                    border: 1px solid #ccc;
                    padding: 10px 40px 30px 40px;
                    color: #131313;
                    font-size: 14px;
                }

                .title-voucher {
                    text-align: center;
                    padding: 0.75rem 0;
                }

                .header-voucher {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.75rem 0;
                }

                .document-copy {
                    font-weight: 800;
                }

                .document-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .text-right {
                    text-align: right;
                }

                .text-center {
                    text-align: center;
                }

                .content {
                    margin-bottom: 10px;
                }

                .content p {
                    text-indent: 3ch;
                }

                .date-voucher {
                    padding: 0.75rem 0;
                }

                .footer-voucher {
                    padding: 0.75rem 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                }

                .signature {
                    border-top: 1px solid #131313;
                    width: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 30px;
                }`;

            iframeDoc.open();
            iframeDoc.write(`
                  <html>
                    <head>
                      <style>${style}</style>
                    </head>
                    <body>
                      ${outerHtmlPaymentVoucher}
                    </body>
                  </html>
                `);
            iframeDoc.close();

            setTimeout(() => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();

                setTimeout(() => document.body.removeChild(iframe), 500);
            }, 100);

        } catch (e) {
            throw new Error('Erro ao gerar recibo de pagamento: ' + e);
        }
    }

    private _outerHtmlPaymentVoucher(data: PaymentVoucherData): string {
        try {
            const formatAmountPaid: string = CurrencyUtil.transform(data.amountPaid, '1.2-2');
            const providerName = data.provider && data.provider.person ? data.provider.person.name : '';
            const companyName = this._company && this._company.person ? this._company.person.name : '';
            const cpfOrCnpjCompany = this._getCpfOrCnpjCompany(data);
            const cpfOrCnpjProvider = this._getCpfOrCnpjProvider(data);
            const amountPaidInFull = this._getAmountPaidInFull(data);
            const formatDateInFull = this._formatDateInFull(data);

            return (`
            <fieldset>
                <h2 class="title-voucher">Recibo de Pagamento</h2>
                <div class="header-voucher">
                    <div class="document-copy">
                        <strong> 1° Via </strong>
                    </div>
                    <div class="document-info">
                        ${!StringUtil.isEmpty(data.documentNumber) ? `<span> N°: <strong> ${data.documentNumber} </strong> </span>` : ''}
                        <span> VALOR: <strong> ${formatAmountPaid} </strong> </span>
                    </div>
                </div>
                <div class="content">
                    <p>
                        Eu,
                        <strong> ${providerName} </strong>
                        declaro que recebi da empresa
                        <strong> ${companyName} </strong>
                        portador do CPF/CNPJ n°
                        <strong> ${cpfOrCnpjCompany} </strong>
                        , a importância de
                        <strong> ${amountPaidInFull} </strong>
                        referente à <strong> ${(data.description) ? data.description : data.comments} </strong>.
                    </p>
                    ${(data.description && data.comments) ? `<span><strong>OBS: </strong>${data.comments}</span>` : ''}
                    <p>
                        Para maior clareza firmo(amos) o presente recibo para que produza os seus efeitos, dando plena, rasa e
                        irrevogável quitação, pelo valor recebido.
                    </p>
                </div>
                <p class='text-right date-voucher'>
                    ${formatDateInFull}
                </p>
                <div class="footer-voucher center">
                    <div class="signature">
                        ${providerName}
                    </div>
                    <div class="document">
                        CPF/CNPJ: ${cpfOrCnpjProvider}
                    </div>
                </div>
            </fieldset>
            `);
        } catch (e) {
            throw new Error('Erro ao consultar a empresa para gerar o recibo de pagamento: ' + e);
        }
    }

    private _getCpfOrCnpjCompany(data: PaymentVoucherData): string {
        if (
            ObjectUtil.isNull(this._company) ||
            (!ObjectUtil.isNull(this._company) && ObjectUtil.isNull(this._company.person))
        ) {
            return '';
        }

        const person = this._company.person;
        if (!ObjectUtil.isNull(person.legalPerson)) {
            return !StringUtil.isEmpty(person.legalPerson.cnpj) ? StringUtil.format(person.legalPerson.cnpj, '00.000.000/0000-00') : '';
        } else if (!ObjectUtil.isNull(person.naturalPerson)) {
            return !StringUtil.isEmpty(person.naturalPerson.cpf) ? StringUtil.format(person.naturalPerson.cpf, '000.000.000-00') : '';
        }

        return '';
    }

    private _getCpfOrCnpjProvider(data: PaymentVoucherData): string {
        if (
            ObjectUtil.isNull(data.provider) ||
            (!ObjectUtil.isNull(data.provider) && ObjectUtil.isNull(data.provider.person))
        ) {
            return '';
        }

        const person = data.provider.person;
        if (!ObjectUtil.isNull(person.legalPerson)) {
            return !StringUtil.isEmpty(person.legalPerson.cnpj) ? StringUtil.format(person.legalPerson.cnpj, '00.000.000/0000-00') : '';
        } else if (!ObjectUtil.isNull(person.naturalPerson)) {
            return !StringUtil.isEmpty(person.naturalPerson.cpf) ? StringUtil.format(person.naturalPerson.cpf, '000.000.000-00') : '';
        }

        return '';
    }

    private _getAmountPaidInFull(data: PaymentVoucherData): string {
        const amountPaidFormatted: string = CurrencyUtil.transform(data.amountPaid, '1.2-2');
        const amountPaidNotCurrency: string = amountPaidFormatted.substring(2).trim();
        return `${amountPaidFormatted} (${amountPaidNotCurrency.numberFullName(true)})`;
    }

    private _formatDateInFull(data: PaymentVoucherData): string {
        const date = new Date(data.paymentDate);
        const dayNumber = date.getDate();
        const year = date.getFullYear();
        const day = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'][date.getDay()];
        const month = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][date.getMonth()];
        return `${day}, ${dayNumber} de ${month} de ${year}`;
    }

}
